import { useRef, useState } from "react";

import { futureMoneyLogo } from "@assets/images";
import { AgreementType } from "@ctypes";
import { useCreateConsent, useCurrentAgreement, useModal, useTheme } from "@hooks";
import { i18n, Translations } from "@i18n";
import { Eyebrow, FilledButton } from "@routes/DashboardRoute/Home/styles";
import { homeCssVars } from "@routes/DashboardRoute/Home/tokens";
import { Intercom } from "@services";
import {
  MigrationBalanceNotice,
  MigrationHeadline,
  MigrationReassurance,
  MigrationSteps,
} from "./components";
import { CAPITAL_GROUP_MIGRATION_FAQ_URL } from "./constants";
import {
  Actions,
  Clickwrap,
  ErrorText,
  Logo,
  Note,
  Stack,
  Surface,
  TextAction,
} from "./styledComponents";

const formatApprovedDate = (date: Date) =>
  date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

/**
 * The 529 migration consent dialog. Approving records the user's e-signature against the Capital
 * Group migration agreement, and the same dialog then becomes the confirmation so the user sees
 * their approval land rather than the dialog simply closing.
 */
export const CapitalGroupMigrationConsentModal = () => {
  const { dismiss } = useModal();
  const { theme } = useTheme();

  // When the dialog opened, recorded against the consent as when the user saw the terms.
  const viewedAt = useRef(new Date()).current;

  const { data: agreementData, loading: agreementLoading } = useCurrentAgreement(
    AgreementType.CapitalGroup_529MigrationTerms,
  );
  const [createConsent, { loading: consenting }] = useCreateConsent();

  const [approvedAt, setApprovedAt] = useState<Date | null>(null);
  const [hasError, setHasError] = useState(false);

  const agreementId = agreementData?.currentAgreement?.id;
  const cssVars = homeCssVars(theme.meta.dark);

  const handleApprove = async () => {
    if (!agreementId) return;

    setHasError(false);

    try {
      const { data } = await createConsent({
        variables: { agreementId, viewedTimestamp: viewedAt.toISOString() },
      });

      const signed = data?.createConsent?.signedTimestamp;
      setApprovedAt(signed ? new Date(signed) : new Date());
    } catch {
      setHasError(true);
    }
  };

  const handleSpeakToAdvisor = () => {
    dismiss();
    Intercom.showNewMessage(i18n.t(Translations.CapitalGroupMigrationAdvisorMessage));
  };

  if (approvedAt) {
    return (
      <Surface style={cssVars} role="dialog" aria-modal="true">
        <Stack>
          <Eyebrow style={{ alignSelf: "stretch" }}>
            {i18n.t(Translations.CapitalGroupMigrationConfirmedEyebrow, {
              date: formatApprovedDate(approvedAt),
            })}
          </Eyebrow>
          <Logo src={futureMoneyLogo} alt="" />
          <MigrationHeadline
            text={i18n.t(Translations.CapitalGroupMigrationConfirmedTitle)}
            highlight={i18n.t(Translations.CapitalGroupMigrationConfirmedTitleHighlight)}
          />
          <MigrationSteps
            steps={[
              {
                title: i18n.t(Translations.CapitalGroupMigrationConfirmedStep1Title),
                subtitle: i18n.t(Translations.CapitalGroupMigrationConfirmedStep1Subtitle),
              },
              {
                title: i18n.t(Translations.CapitalGroupMigrationConfirmedStep2Title),
                subtitle: i18n.t(Translations.CapitalGroupMigrationConfirmedStep2Subtitle),
              },
              {
                title: i18n.t(Translations.CapitalGroupMigrationConfirmedStep3Title),
                subtitle: i18n.t(Translations.CapitalGroupMigrationConfirmedStep3Subtitle),
              },
            ]}
          />
          <MigrationBalanceNotice />
          <Note>{i18n.t(Translations.CapitalGroupMigrationContributionsUnchanged)}</Note>
        </Stack>

        <Actions>
          <FilledButton style={{ justifyContent: "center" }} onClick={dismiss}>
            {i18n.t(Translations.CapitalGroupMigrationConfirmedPrimaryCTA)}
          </FilledButton>
          <TextAction
            as="a"
            href={CAPITAL_GROUP_MIGRATION_FAQ_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            {i18n.t(Translations.CapitalGroupMigrationConfirmedSecondaryCTA)}
          </TextAction>
        </Actions>
      </Surface>
    );
  }

  return (
    <Surface style={cssVars} role="dialog" aria-modal="true">
      <Stack>
        <Logo src={futureMoneyLogo} alt="" />
        <MigrationHeadline
          text={i18n.t(Translations.CapitalGroupMigrationConsentTitle)}
          highlight={i18n.t(Translations.CapitalGroupMigrationConsentTitleHighlight)}
        />
        <MigrationReassurance
          items={[
            i18n.t(Translations.CapitalGroupMigrationConsentReassuranceContributions),
            i18n.t(Translations.CapitalGroupMigrationConsentReassuranceEmail),
          ]}
        />
      </Stack>

      <Actions>
        <Clickwrap>{i18n.t(Translations.CapitalGroupMigrationConsentClickwrap)}</Clickwrap>
        {hasError ? <ErrorText>{i18n.t(Translations.CapitalGroupMigrationConsentError)}</ErrorText> : null}
        <FilledButton
          style={{ justifyContent: "center" }}
          onClick={handleApprove}
          // No agreement row means there is nothing to consent to yet, so the button stays inert.
          disabled={agreementLoading || consenting || !agreementId}
          aria-busy={consenting}
        >
          {i18n.t(Translations.CapitalGroupMigrationConsentPrimaryCTA)}
        </FilledButton>
        <TextAction type="button" onClick={handleSpeakToAdvisor}>
          {i18n.t(Translations.CapitalGroupMigrationConsentSecondaryCTA)}
        </TextAction>
      </Actions>
    </Surface>
  );
};
